import { Button, FileUpload, Image, Text } from '@chakra-ui/react';
import { MaskIcon } from '@ui/components/MaskIcon';
import { Num } from '@ui/components/Num';
import { Fraction } from 'bi-fraction';
import { useMemo, useRef, useState } from 'react';

import { InDesktop } from '../MobileOrDesktop';

export const ImageUpload = ({
  setImage,
  preview,
  maxSizeKb = 500,
}: {
  setImage: (image: File | null) => void;
  maxSizeKb?: number;
  preview?: string;
}) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFileChange = (files: File[]) => {
    if (!files.length) {
      return;
    }
    const file = files[0];
    if (file.type.startsWith('image/')) {
      if (maxSizeKb && file.size <= maxSizeKb * 1024) {
        setImageFile(file);
        setImage(file);
        setHasError(false);
      } else {
        setImage(null);
        setImageFile(null);
        setHasError(true);
      }
    }
  };
  const previewImageSrc = useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : preview),
    [imageFile, preview],
  );
  const previewImageName = useMemo(
    () => (imageFile ? imageFile.name : preview?.split('/').pop()),
    [imageFile, preview],
  );

  return (
    <>
      <FileUpload.Root
        variant={hasError ? 'error' : undefined}
        accept="image/png, image/jpeg"
        onFileAccept={(details) => {
          handleFileChange(details.files);
        }}
      >
        <FileUpload.HiddenInput ref={inputRef} />
        <FileUpload.Dropzone>
          {previewImageSrc ? (
            <FileUpload.DropzoneContent>
              <Image
                width="70px"
                height="70px"
                flexShrink="0"
                src={previewImageSrc}
                alt={previewImageName}
              />
              <Text
                wordBreak="break-all"
                lineClamp={1}
                textStyle="body2Bold"
                color="text.default"
              >
                {previewImageName}
              </Text>
              {imageFile && (
                <Num
                  value={new Fraction(imageFile.size, 1024)}
                  suffix="KB"
                  textStyle="caption"
                />
              )}
            </FileUpload.DropzoneContent>
          ) : (
            <FileUpload.DropzoneContent>
              <MaskIcon
                icon="url(/masks/upload.svg)"
                bgColor="currentcolor"
                boxSize="24px"
              />
              {!hasError && (
                <Text textStyle="body2">Upload their profile pic</Text>
              )}
              {hasError && (
                <Text textStyle="body2" color="black">
                  Something&apos;s wrong. Try again.
                </Text>
              )}
              <Text textStyle="caption">JPG/PNG (Max. {maxSizeKb}KB)</Text>
            </FileUpload.DropzoneContent>
          )}
        </FileUpload.Dropzone>
      </FileUpload.Root>
      <InDesktop>
        <Button variant="secondary" onClick={() => inputRef.current?.click()}>
          Choose file
        </Button>
      </InDesktop>
    </>
  );
};
